import React, { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Footer from '../components/Footer';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../context/LanguageContext';
import styles from './PaymentPage.module.css';
import Navbar, { BrandIcon } from '../components/Navbar';
import { toast } from 'react-toastify';
import { getApiUrl } from '../api';

const getCachedSelectedLocationCount = (incoming) => {
  if (Array.isArray(incoming.selectedLocations)) return incoming.selectedLocations.length;
  if (Number.isInteger(incoming.selectedLocationCount) && incoming.selectedLocationCount >= 0) {
    return incoming.selectedLocationCount;
  }

  try {
    const selectedLocations = JSON.parse(localStorage.getItem('gbp_selected_locations'));
    return Array.isArray(selectedLocations) ? selectedLocations.length : null;
  } catch {
    return null;
  }
};

const getPlanForLocationCount = (availablePlans, locationCount) => {
  const agencyPlan = availablePlans.find(plan => plan.planKey === 'agency');
  const standardPlans = availablePlans
    .filter(plan => plan.planKey !== 'agency')
    .filter(plan => Number.isFinite(Number(plan.limits?.locations)) &&
      Number(plan.limits.locations) >= locationCount)
    .sort((left, right) => Number(left.limits.locations) - Number(right.limits.locations));

  if (standardPlans.length) return standardPlans[0].planKey;

  const agencyMinimum = Number(agencyPlan?.limits?.locations);
  return agencyPlan && Number.isFinite(agencyMinimum) && locationCount >= agencyMinimum
    ? agencyPlan.planKey
    : null;
};

const PaymentPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const incoming = location.state || {};
  const incomingLocationCount = Array.isArray(incoming.selectedLocations)
    ? incoming.selectedLocations.length
    : Number.isInteger(incoming.selectedLocationCount) && incoming.selectedLocationCount >= 0
      ? incoming.selectedLocationCount
      : null;
  const fallbackPlan = ['starter', 'growth', 'pro', 'agency'].includes(incoming.plan)
    ? incoming.plan
    : 'growth';
  const hasManuallySelectedPlan = useRef(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [plans, setPlans] = useState([]);
  const [selectedLocationCount, setSelectedLocationCount] = useState(
    () => getCachedSelectedLocationCount(incoming)
  );
  const [locationLookupComplete, setLocationLookupComplete] = useState(
    () => !localStorage.getItem('token1')
  );
  const [locationsLoading, setLocationsLoading] = useState(
    () => Boolean(localStorage.getItem('token1'))
  );
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingError, setPricingError] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(
    fallbackPlan
  );
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    let isMounted = true;
    const loadPricing = async () => {
      try {
        const res = await fetch(getApiUrl('/api/pricing'), { cache: 'no-store' });
        if (!res.ok) throw new Error(`Pricing request failed (${res.status}).`);
        const data = await res.json();
        const availablePlans = Array.isArray(data.packages) ? data.packages : [];
        if (availablePlans.length !== 4 ||
            availablePlans.map(item => item.planKey).join(',') !== 'starter,growth,pro,agency' ||
            availablePlans.some(item =>
              item.paymentMethod?.monthly === null ||
              item.paymentMethod?.monthly === undefined ||
              item.paymentMethod?.annually === null ||
              item.paymentMethod?.annually === undefined ||
              !Number.isFinite(Number(item.paymentMethod?.monthly)) ||
              !Number.isFinite(Number(item.paymentMethod?.annually))
            )) {
          throw new Error('Pricing response did not contain the current plans.');
        }
        if (isMounted) {
          setPlans(availablePlans);
          setSelectedPlan(current => availablePlans.some(item => item.planKey === current)
            ? current
            : 'growth');
        }
      } catch (err) {
        console.error('Payment pricing fetch error:', err);
        if (isMounted) setPricingError(true);
      } finally {
        if (isMounted) setPricingLoading(false);
      }
    };
    loadPricing();
    return () => { isMounted = false; };
  }, []);

  React.useEffect(() => {
    if (incomingLocationCount !== null) {
      setSelectedLocationCount(incomingLocationCount);
      setLocationLookupComplete(true);
      return;
    }

    const token = localStorage.getItem('token1');
    if (!token) return;

    let isMounted = true;
    const loadSelectedLocations = async () => {
      try {
        const res = await fetch(getApiUrl('/api/auth/google/locations'), {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        });
        if (!res.ok) throw new Error(`Location request failed (${res.status}).`);
        const data = await res.json();
        const locationRecords = Array.isArray(data.locations) ? data.locations : [];
        const selectedLocationIds = new Set(
          locationRecords.flatMap(record =>
            Array.isArray(record.selectedLocations)
              ? record.selectedLocations
                .map(selectedLocation => selectedLocation?.locationId)
                .filter(Boolean)
              : []
          )
        );
        const count = selectedLocationIds.size;
        if (isMounted) setSelectedLocationCount(count);
      } catch (err) {
        console.error('Payment selected locations fetch error:', err);
      } finally {
        if (isMounted) setLocationLookupComplete(true);
      }
    };
    loadSelectedLocations();
    return () => { isMounted = false; };
  }, [incomingLocationCount]);

  React.useEffect(() => {
    if (!locationLookupComplete || !plans.length) return;
    if (!hasManuallySelectedPlan.current && Number.isInteger(selectedLocationCount)) {
      if (selectedLocationCount === 0) {
        setSelectedPlan(fallbackPlan);
      } else {
        const recommendedPlan = getPlanForLocationCount(plans, selectedLocationCount);
        setSelectedPlan(recommendedPlan || '');
      }
    }
    setLocationsLoading(false);
  }, [fallbackPlan, locationLookupComplete, plans, selectedLocationCount]);

  const planDetails = plans.find(item => item.planKey === selectedPlan);
  const agencyMinimum = Number(plans.find(plan => plan.planKey === 'agency')?.limits?.locations);
  const maxStandardLocations = Math.max(
    0,
    ...plans
      .filter(plan => plan.planKey !== 'agency')
      .map(plan => Number(plan.limits?.locations))
      .filter(Number.isFinite)
  );
  const requiresCustomPricing = Number.isInteger(selectedLocationCount) &&
    selectedLocationCount > maxStandardLocations &&
    Number.isFinite(agencyMinimum) &&
    selectedLocationCount < agencyMinimum;
  const currency = planDetails?.paymentMethod?.currency || 'AED';
  const annualPrice = planDetails?.paymentMethod?.annually;
  const activePrice = billingCycle === 'monthly'
    ? planDetails?.paymentMethod?.monthly
    : (annualPrice == null ? null : annualPrice / 12);
  const formatPrice = amount => Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(Number(amount)) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  const selectedDescription = planDetails?.descriptionPoints?.[0];
  const planDescription = typeof selectedDescription === 'string'
    ? selectedDescription
    : selectedDescription?.en || selectedDescription?.ar || '';

  const validateEmail = () => {
    if (!email) {
      setEmailError('Email address is required.');
      return false;
    }
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regex.test(email)) {
      setEmailError('Please enter a valid email address.');
      return false;
    }
    setEmailError('');
    return true;
  };

  const startCheckout = async () => {
    if (locationsLoading || requiresCustomPricing || !planDetails || activePrice == null) {
      toast.error('Current pricing is unavailable. Please try again shortly.');
      return;
    }
    if (!validateEmail()) {
      toast.error('Please enter a valid email for the receipt.');
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token1');

      const res = await fetch(getApiUrl('/api/payment/checkout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          plan:          selectedPlan,
          billingCycle,
          email,
          auditRequestId: incoming.auditRequestId || null,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.message || 'Could not start checkout. Please try again.');
        return;
      }

      if (!data.checkoutUrl) {
        toast.error('No checkout URL returned. Please try again.');
        return;
      }

      // Redirect to Creem-hosted checkout page
      window.location.href = data.checkoutUrl;
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <Navbar commonNavigation className={styles.navbar} innerClassName={styles.navInner} brandClassName={styles.navBrand} brand={<BrandIcon size={24} style={{ marginRight: 6 }} />}>
          <LanguageToggle />
      </Navbar>

      <main className={styles.mainContainer}>
        <div className={styles.grid}>
          
          {/* LEFT PANEL — Value Props & Guarantees */}
          <div className={styles.trustPanel}>
            <div>
              <h2 className={styles.trustTitle}>{t('completeSubscription', 'Complete your subscription')}</h2>
              <p style={{ color: '#9ca3af', marginTop: 8, fontSize: '0.95rem' }}>
                {t('trustPanelSub', 'Join hundreds of local businesses that trust ReplyShoot to keep their reviews active and build customer trust.')}
              </p>
            </div>

            <div className={styles.bulletList}>
              <div className={styles.bulletItem}>
                <div className={styles.bulletIcon}>✓</div>
                <div className={styles.bulletText}>
                  <strong>{t('guaranteeTitle', 'Cancel Anytime, No Contracts')}</strong><br />
                  {t('guaranteeText', "You're never locked in - cancel your subscription at any time directly from your dashboard.")}
                </div>
              </div>

              <div className={styles.bulletItem}>
                <div className={styles.bulletIcon}>✓</div>
                <div className={styles.bulletText}>
                  <strong>{t('copywritersTitle', 'Personalized, Not Generic')}</strong><br />
                  {t('copywritersText', "Every reply is AI-drafted and matched to your brand's unique voice — never a generic template.")}
                </div>
              </div>

              <div className={styles.bulletItem}>
                <div className={styles.bulletIcon}>✓</div>
                <div className={styles.bulletText}>
                  <strong>{t('controlTitle', 'You Keep Full Control')}</strong><br />
                  {t('controlText', 'No reply is ever published without your approval. Verify every draft with one click via email.')}
                </div>
              </div>
            </div>

            <div className={styles.securityFooter}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>
                {t('secureCheckoutCreem', 'Secure checkout powered by Creem. Your payment details are fully encrypted and PCI-DSS compliant.')}
              </span>
            </div>
          </div>

          {/* RIGHT PANEL — Interactive Pricing & Checkout */}
          <div className={styles.card}>
            <h3 className={styles.title} style={{ fontSize: '1.4rem' }}>{t('orderSummary', 'Order Summary')}</h3>
            {pricingLoading && <p role="status">Loading current plans…</p>}
            {pricingError && (
              <div className={styles.pricingError} role="alert">
                Current pricing is temporarily unavailable. Please try again shortly.
              </div>
            )}
            {!pricingLoading && !pricingError && !locationsLoading && requiresCustomPricing && (
              <div className={styles.contactSalesNotice} role="status">
                <p>
                  {t(
                    'contactSalesForCustomPricing',
                    `For ${selectedLocationCount} locations, please contact our sales team to find a suitable plan and custom pricing.`
                  )}
                </p>
                <a href={`mailto:support@replyshoot.com?subject=${encodeURIComponent(`Custom pricing for ${selectedLocationCount} locations`)}`}>
                  {t('contactSales', 'Contact sales')}
                </a>
              </div>
            )}

            {/* Cycle Pill Switcher */}
            {!pricingLoading && !pricingError && !locationsLoading && planDetails && (
              <div className={styles.cycleSwitcher}>
              <button
                type="button"
                className={`${styles.cycleBtn} ${billingCycle === 'monthly' ? styles.cycleBtnActive : ''}`}
                onClick={() => setBillingCycle('monthly')}
              >
                {t('monthlyLabel', 'Monthly')}
              </button>
              <button
                type="button"
                className={`${styles.cycleBtn} ${billingCycle === 'annual' ? styles.cycleBtnActive : ''}`}
                onClick={() => setBillingCycle('annual')}
                disabled={annualPrice == null}
              >
                {t('billedAnnually', 'Billed Annually')}
                {planDetails.paymentMethod?.annualSaving != null && (
                  <span className={styles.saveBadge}>
                    {t('saveAmount', `Save ${currency} ${formatPrice(planDetails.paymentMethod.annualSaving)}`)}
                  </span>
                )}
              </button>
              </div>
            )}

            {/* Selected Plan Details */}
            {!pricingLoading && !pricingError && !locationsLoading && planDetails && (
              <div className={styles.planSummaryCard}>
              <div className={styles.planHeader}>
                <label className={styles.planSelectLabel}>
                  <span className={styles.planBadge}>{planDetails.name}</span>
                  <select
                    className={styles.planSelect}
                    aria-label={t('chooseYourPlan', 'Choose your plan')}
                    value={selectedPlan}
                    onChange={event => {
                      hasManuallySelectedPlan.current = true;
                      setSelectedPlan(event.target.value);
                    }}
                  >
                    {plans.map(item => (
                      <option key={item.planKey} value={item.planKey}>{item.name}</option>
                    ))}
                  </select>
                </label>
                <div className={styles.planPrice}>
                  {currency} {formatPrice(activePrice)}
                  <span className={styles.planPriceSub}>{t('perMonth', '/mo')}</span>
                </div>
              </div>
              <p className={styles.planDetails}>{planDescription}</p>
              {billingCycle === 'annual' && (
                <p className={styles.planDetails}>
                  {t('billedAsOneCharge', `Billed as one annual charge of ${currency} ${formatPrice(annualPrice)}`)}
                </p>
              )}
              </div>
            )}

            {/* Receipt Email Address */}
            {!requiresCustomPricing && (
              <div className={styles.formGroup}>
              <label className={styles.label}>{t('receiptEmail', 'Receipt Email Address')}</label>
              <div className={styles.inputWrapper}>
                <span className={styles.inputIcon}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </span>
                <input
                  type="email"
                  className={`${styles.input} ${emailError ? styles.inputError : ''}`}
                  value={email}
                  onChange={e => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                  placeholder={t('receiptEmailPlaceholder', 'you@yourbusiness.com')}
                  required
                />
              </div>
              {emailError && (
                <span className={styles.inputErrorMessage}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {emailError}
                </span>
              )}
              </div>
            )}

            {/* CTA Actions */}
            <div className={styles.btnRow}>
              {!requiresCustomPricing && <button
                type="button"
                className={styles.checkoutBtn}
                onClick={startCheckout}
                disabled={isLoading || pricingLoading || locationsLoading || pricingError || !planDetails || activePrice == null}
              >
                {isLoading
                  ? t('redirectingToCheckout', 'Redirecting to Checkout…')
                  : t('continueToCheckout', 'Continue to Secure Checkout')}
                {!isLoading && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
                  </svg>
                )}
              </button>}
              <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                {t('goBack', 'Go Back')}
              </button>
            </div>

          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentPage;
